/**
 * Helper centralizzato per caricare la griglia dei subprojects via AJAX
 * 
 * Questo helper gestisce:
 * - Caricamento della griglia via AJAX
 * - Estrazione ed esecuzione degli script dalla risposta
 * - Estrazione ed inserimento degli stili nel <head> del documento
 * - Reinizializzazione corretta della griglia
 * 
 * Utilizzo:
 *   SubprojectsGridLoader.load(projectId, containerSelector, options)
 * 
 * Esempio:
 *   SubprojectsGridLoader.load(649, '#tasks-container', {
 *     onSuccess: function() { console.log('Griglia caricata!'); },
 *     onError: function(error) { console.error('Errore:', error); }
 *   })
 */

window.SubprojectsGridLoader = (function() {
    'use strict';

    /**
     * Carica la griglia dei subprojects via AJAX
     * 
     * @param {number} projectId - ID del progetto
     * @param {string} containerSelector - Selettore jQuery del container dove inserire la griglia
     * @param {object} options - Opzioni di configurazione
     * @param {function} options.onSuccess - Callback chiamato quando la griglia è caricata con successo
     * @param {function} options.onError - Callback chiamato in caso di errore
     * @param {string} options.loadingHtml - HTML da mostrare durante il caricamento
     */
    function load(projectId, containerSelector, options) {
        options = options || {};
        
        const $container = $(containerSelector);
        
        if ($container.length === 0) {
            console.error('SubprojectsGridLoader: Container non trovato:', containerSelector);
            if (options.onError) {
                options.onError('Container non trovato');
            }
            return;
        }

        // Mostra loading indicator
        const loadingHtml = options.loadingHtml || '<div style="text-align: center; padding: 40px;"><i class="fa fa-spinner fa-spin" style="font-size: 24px; color: #3c8dbc;"></i><p style="margin-top: 15px; color: #666;">Caricamento griglia task...</p></div>';
        $container.html(loadingHtml);

        // Costruisci URL - usa window.subprojectsGridUrl se disponibile (passato dalla view Blade)
        // altrimenti usa l'URL hardcoded
        // Aggiungi parametro my_brain=1 se viene chiamato da my-brain
        const isMyBrain = options.myBrainMode || false;
        const archivedFilter = options.archivedFilter ?? '0';
        const sheetId = options.sheetId ?? null;
        const baseUrl = window.subprojectsGridUrl || '/subprojects/grid';
        const params = [];
        if (isMyBrain) {
            params.push('my_brain=1');
        }
        if (archivedFilter === '1') {
            params.push('archived_filter=1');
        } else if (archivedFilter === '' || archivedFilter === 'all') {
            params.push('archived_filter=all');
        } else if (archivedFilter !== '0') {
            params.push('archived_filter=' + encodeURIComponent(archivedFilter));
        }
        if (sheetId && sheetId !== 'all') {
            params.push('sheet_id=' + encodeURIComponent(sheetId));
        }
        const query = params.length ? '?' + params.join('&') : '';
        const url = `${baseUrl}/${projectId}${query}`;
        
        // Carica la griglia tramite AJAX
        $.ajax({
            url: url,
            method: 'GET',
            headers: {
                'X-CSRF-TOKEN': $('meta[name="csrf-token"]').attr('content')
            },
            success: function(html) {
                try {
                    // Un solo parse dell'HTML (la risposta può essere grande): estrae script e
                    // stili e produce l'HTML ripulito in un'unica passata, invece di costruire
                    // ~4 DOM temporanei (extractScripts/extractStyles/removeScripts/removeStyles).
                    const parsed = parseGridResponse(html);
                    const scripts = parsed.scripts;
                    const styles = parsed.styles;
                    const htmlWithoutScriptsAndStyles = parsed.html;
                    console.log('SubprojectsGridLoader: Script estratti:', scripts.length);
                    console.log('SubprojectsGridLoader: Stili estratti:', styles.length);
                    
                    // Aggiungi v-cloak al container per evitare che Vue compili il contenuto
                    $container.attr('v-cloak', '');
                    
                    // Marca che siamo in un contesto AJAX per evitare inizializzazione automatica
                    window.subprojectsGridAjaxLoad = true;
                    
                    // Inserisci gli stili nel <head> prima di inserire l'HTML
                    insertStyles(styles);
                    
                    // Inserisci l'HTML senza script e stili
                    $container.html(htmlWithoutScriptsAndStyles);
                    
                    // Esegui gli script estratti
                    executeScripts(scripts, function() {
                        // Reinizializza la griglia dopo che gli script sono stati eseguiti
                        reinitializeGrid();
                        
                        // Rimuovi v-cloak dopo che tutto è stato inizializzato
                        setTimeout(function() {
                            $container.removeAttr('v-cloak');
                            // Rimuovi il flag AJAX dopo l'inizializzazione
                            window.subprojectsGridAjaxLoad = false;
                        }, 100);
                        
                        // Ripristina filtro foglio da localStorage dopo reload AJAX
                        if (window.TaskSheetsGrid && window.__taskSheetsConfig && window.__taskSheetsConfig.showTabs) {
                            const $wrapper = $container.find('.task-grid-inner-wrapper').first();
                            if ($wrapper.length) {
                                window.TaskSheetsGrid.initTaskSheetsForWrapper($wrapper, window.__taskSheetsConfig);
                            }
                        }

                        // Callback di successo
                        if (options.onSuccess) {
                            options.onSuccess();
                        }
                    });
                } catch (error) {
                    console.error('SubprojectsGridLoader: Errore durante l\'elaborazione della risposta:', error);
                    $container.html('<div style="text-align: center; padding: 40px; color: #d9534f;"><i class="fa fa-exclamation-triangle" style="font-size: 24px; margin-bottom: 15px;"></i><p>Errore durante il caricamento della griglia</p></div>');
                    if (options.onError) {
                        options.onError(error);
                    }
                }
            },
            error: function(xhr, status, error) {
                console.error('SubprojectsGridLoader: Errore AJAX:', status, error);
                
                let errorMsg = 'Errore nel caricamento della griglia task';
                if (xhr.responseJSON && xhr.responseJSON.error) {
                    errorMsg = xhr.responseJSON.error;
                } else if (xhr.status === 403) {
                    errorMsg = 'Non hai i permessi per visualizzare i task di questo progetto';
                } else if (xhr.status === 404) {
                    errorMsg = 'Progetto non trovato';
                }
                
                $container.html('<div style="text-align: center; padding: 40px; color: #d9534f;"><i class="fa fa-exclamation-triangle" style="font-size: 24px; margin-bottom: 15px;"></i><p>' + errorMsg + '</p></div>');
                
                if (options.onError) {
                    options.onError(error);
                }
            }
        });
    }

    /**
     * Esegue un solo parse dell'HTML di risposta ed estrae in un'unica passata:
     * gli script (src/text/type), gli stili (text/type) e l'HTML ripulito da
     * <script> e <style>. Evita di ricostruire più DOM temporanei sull'intero
     * payload (che può essere di alcuni MB).
     *
     * @param {string} html - HTML grezzo della risposta
     * @returns {{scripts: Array, styles: Array, html: string}}
     */
    function parseGridResponse(html) {
        const tempDiv = $('<div>').html(html);

        const scripts = [];
        tempDiv.find('script').each(function() {
            scripts.push({
                src: $(this).attr('src'),
                text: $(this).html() || $(this).text() || '',
                type: $(this).attr('type') || 'text/javascript'
            });
        });

        const styles = [];
        tempDiv.find('style').each(function() {
            styles.push({
                text: $(this).html() || $(this).text() || '',
                type: $(this).attr('type') || 'text/css'
            });
        });

        tempDiv.find('script, style').remove();

        return {
            scripts: scripts,
            styles: styles,
            html: tempDiv.html()
        };
    }

    /**
     * Inserisce gli stili estratti nel <head> del documento
     * 
     * @param {Array} styles - Array di oggetti style
     */
    function insertStyles(styles) {
        styles.forEach(function(styleObj, index) {
            // Crea un ID univoco per lo stile per evitare duplicati
            const styleId = 'subprojects-grid-style-' + Date.now() + '-' + index;
            
            // Verifica se lo stile è già stato inserito
            if (document.getElementById(styleId)) {
                return; // Salta se già presente
            }
            
            const style = document.createElement('style');
            style.id = styleId;
            style.type = styleObj.type || 'text/css';
            style.textContent = styleObj.text;
            
            document.head.appendChild(style);
        });
    }

    /**
     * Esegue gli script estratti in sequenza
     * 
     * @param {Array} scripts - Array di oggetti script
     * @param {function} callback - Callback chiamato quando tutti gli script sono stati eseguiti
     */
    function executeScripts(scripts, callback) {
        if (scripts.length === 0) {
            if (callback) callback();
            return;
        }

        let scriptIndex = 0;
        
        function executeNext() {
            if (scriptIndex >= scripts.length) {
                if (callback) callback();
                return;
            }

            const scriptObj = scripts[scriptIndex];
            
            if (scriptObj.src) {
                // Script esterno - carica normalmente
                const script = document.createElement('script');
                script.type = scriptObj.type || 'text/javascript';
                script.src = scriptObj.src;
                script.onload = function() {
                    scriptIndex++;
                    executeNext();
                };
                script.onerror = function() {
                    console.warn('SubprojectsGridLoader: Errore nel caricamento script esterno:', scriptObj.src);
                    scriptIndex++;
                    executeNext();
                };
                document.head.appendChild(script);
            } else {
                // Script inline - esegui direttamente con eval invece di inserire nel DOM
                // Questo evita che Vue.js provi a compilare gli script
                try {
                    
                    // Pulisci il contenuto dello script rimuovendo eventuali tag HTML o commenti Blade non processati
                    let cleanedText = scriptObj.text;
                    
                    // Rimuovi eventuali tag HTML o commenti Blade che potrebbero essere stati inclusi
                    cleanedText = cleanedText.replace(/<!--.*?-->/g, ''); // Rimuovi commenti HTML
                    cleanedText = cleanedText.replace(/@php[\s\S]*?@endphp/g, ''); // Rimuovi blocchi @php non processati
                    cleanedText = cleanedText.trim();
                    
                    // Verifica se il contenuto inizia con '<' (potrebbe essere HTML non processato)
                    if (cleanedText.startsWith('<')) {
                        console.error('SubprojectsGridLoader: ⚠️ Il contenuto dello script inizia con "<" - potrebbe essere HTML non processato');
                        console.error('SubprojectsGridLoader: Contenuto script (primi 500 caratteri):', cleanedText.substring(0, 500));
                        scriptIndex++;
                        executeNext();
                        return;
                    }
                    
                    // Usa Function constructor invece di eval per maggiore sicurezza
                    // Usa waitForjQuery per aspettare che jQuery sia disponibile prima di eseguire
                    // Helper standardizzato per jQuery readiness - Pattern utilizzato nel progetto
                    function waitForjQuery(callback, attempts = 0) {
                        const maxAttempts = 100; // 10 secondi massimi
                        
                        if (typeof window.jQuery === 'undefined' || typeof window.$ === 'undefined') {
                            if (attempts < maxAttempts) {
                                setTimeout(() => waitForjQuery(callback, attempts + 1), 100);
                            } else {
                                console.error('SubprojectsGridLoader: jQuery non disponibile dopo ' + maxAttempts + ' tentativi, salto esecuzione script inline', scriptIndex + 1);
                                scriptIndex++;
                                executeNext();
                            }
                            return;
                        }
                        
                        callback();
                    }
                    
                    // Aspetta che jQuery sia disponibile prima di eseguire lo script
                    waitForjQuery(function() {
                        try {
                            // Assicurati che $ e jQuery siano disponibili nel contesto globale prima di eseguire
                            if (typeof window.$ === 'undefined' && typeof window.jQuery !== 'undefined') {
                                window.$ = window.jQuery;
                            }
                            if (typeof window.jQuery === 'undefined' && typeof window.$ !== 'undefined') {
                                window.jQuery = window.$;
                            }
                            
                            // Esegui il codice direttamente usando eval invece di new Function
                            // Questo evita problemi di sintassi quando il codice contiene definizioni di funzione
                            // Il codice viene eseguito nel contesto globale, quindi le funzioni definite saranno disponibili globalmente
                            eval(cleanedText);
                            scriptIndex++;
                            executeNext();
                        } catch (e) {
                            console.error('SubprojectsGridLoader: ❌ Errore nell\'esecuzione script inline', scriptIndex + 1, ':', e);
                            console.error('SubprojectsGridLoader: Contenuto script (primi 500 caratteri):', scriptObj.text.substring(0, 500));
                            scriptIndex++;
                            executeNext();
                        }
                    });
                } catch (e) {
                    console.error('SubprojectsGridLoader: ❌ Errore nell\'esecuzione script inline', scriptIndex + 1, ':', e);
                    console.error('SubprojectsGridLoader: Contenuto script (primi 500 caratteri):', scriptObj.text.substring(0, 500));
                    scriptIndex++;
                    executeNext();
                }
            }
        }

        executeNext();
    }

    /**
     * Reinizializza la griglia dopo il caricamento via AJAX
     */
    function reinitializeGrid() {
        // Evita chiamate multiple
        if (window.subprojectsGridReinitializing) {
            return;
        }
        window.subprojectsGridReinitializing = true;
        
        // Aspetta un po' per assicurarsi che tutto sia caricato
        // Usa un polling per verificare che le funzioni siano disponibili
        let attempts = 0;
        const maxAttempts = 20; // Massimo 2 secondi (20 * 100ms)
        
        function tryReinitialize() {
            attempts++;
            
            // Resetta il flag di inizializzazione per permettere la reinizializzazione
            if (typeof window.subprojectsGridInitialized !== 'undefined') {
                window.subprojectsGridInitialized = false;
            }
            
            // Resetta anche il flag delle funzioni inizializzate
            if (typeof window.subprojectsGridFunctionsInitialized !== 'undefined') {
                window.subprojectsGridFunctionsInitialized = false;
            }
            
            // Verifica se le funzioni sono disponibili
            if (typeof window.initializeSubprojectsGridJQueryCode === 'function') {
                try {
                    window.initializeSubprojectsGridJQueryCode(0, true); // Passa true per forzare reinizializzazione
                    
                    // Rimuovi il flag dopo un delay per permettere future reinizializzazioni
                    setTimeout(function() {
                        window.subprojectsGridReinitializing = false;
                    }, 1000);
                } catch (e) {
                    console.error('SubprojectsGridLoader: ❌ Errore nella reinizializzazione griglia:', e);
                    window.subprojectsGridReinitializing = false;
                }
            } else if (attempts < maxAttempts) {
                // Funzione non ancora disponibile, riprova dopo 100ms
                setTimeout(tryReinitialize, 100);
            } else {
                // Funzione non disponibile dopo tutti i tentativi
                console.error('SubprojectsGridLoader: ❌ initializeSubprojectsGridJQueryCode non disponibile dopo', maxAttempts, 'tentativi');
                console.error('SubprojectsGridLoader: Verifica che gli script della griglia siano stati eseguiti correttamente');
                window.subprojectsGridReinitializing = false;
            }
        }
        
        // Inizia il polling dopo un breve delay iniziale
        setTimeout(tryReinitialize, 200);
    }

    // API pubblica
    return {
        load: load
    };
})();

